import {
  ITypeFieldFilterOptions,
  OperatorType,
  PermitFieldType,
} from '~/client/graph'
import { getAllowedFilterValues } from '~/client/src/shared/constants/FilterPropertyMaps/getAllowedFilterValues'
import { locationFilterPropMap } from '~/client/src/shared/constants/FilterPropertyMaps/LocationFilterPropMap'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import { getResourceTypeIdForFieldType } from '~/client/src/shared/utils/permitFieldToResourceType'

/** One resource field of one workflow type, as the Workflow Types selector lists it. */
export interface IResourceFieldAllowance {
  /** Row identity. Composite because a field id is only unique within its type. */
  id: string
  typeId: string
  fieldId: string
  fieldType: PermitFieldType
  /** "1.3", or "1.3.2" for a value inside a conditional field. */
  ordinal: string
  label: string
  /** Whether THIS resource passes the field's static id filters. */
  isAllowed: boolean
  /** True when the field carries no id filter at all, so every resource passes. */
  isUnrestricted: boolean
  /**
   * Whether THIS resource still passes once its WR toggle is turned on: enabling adds it to
   * the field's deny-list, if there is one, unless the allow-list names it.
   */
  isAllowedOnceEnabled: boolean
}

export const makeFieldRowId = (typeId: string, fieldId: string): string =>
  `${typeId}:${fieldId}`

/**
 * Whether one record passes a field's static id filters - the same entries "Allow to select"
 * reads and writes, on propertyPath `id` for all three resource families.
 *
 * Mirrors what `evaluateFiltersWithLbsContext` does with these two entries: an allow-list
 * must name the record, a deny-list must not, and an entry with no values is the same as no
 * entry (empty means unrestricted everywhere, on both sides). The LBS arm is deliberately
 * left out - it restricts by hierarchy at fill time and says nothing about what this field
 * was configured to allow.
 */
export function readAllowance(
  filterOptions: ITypeFieldFilterOptions[] | undefined,
  recordId: string,
): {
  isAllowed: boolean
  isUnrestricted: boolean
  isAllowedOnceEnabled: boolean
} {
  const allowed = getAllowedFilterValues(
    filterOptions,
    locationFilterPropMap.ID,
  )
  const denied = getAllowedFilterValues(
    filterOptions,
    locationFilterPropMap.ID,
    OperatorType.NotIn,
  )

  const isAllowed =
    (!allowed.length || allowed.includes(recordId)) &&
    !denied.includes(recordId)
  // Any static NotIn entry, empty or not: the server extends the first one it finds.
  const hasDenyList = !!filterOptions?.some(
    option =>
      option?.propertyPath === locationFilterPropMap.ID &&
      !option?.fieldId &&
      option?.operator === OperatorType.NotIn,
  )

  return {
    isUnrestricted: !allowed.length && !denied.length,
    isAllowed,
    isAllowedOnceEnabled:
      isAllowed && (!hasDenyList || allowed.includes(recordId)),
  }
}

/**
 * Every field of `resourceTypeId` on one workflow type, numbered the way the rest of the app
 * numbers workflow fields.
 *
 * The numbering follows the existing "1.3" convention: steps that contain no field of this
 * resource type are skipped entirely rather than consuming a number, and fields are numbered
 * within their own step. A value inside a conditional field takes a third component, because
 * it has no position in `step.fields` to borrow - its parent's index stands in for it.
 */
export function collectResourceFields(
  type: WorkflowType,
  resourceTypeId: string,
  recordId: string,
): IResourceFieldAllowance[] {
  const rows: IResourceFieldAllowance[] = []
  let stepNumber = 0

  const belongsHere = (field): boolean =>
    !!field?.type &&
    getResourceTypeIdForFieldType(field.type) === resourceTypeId

  const push = (field, ordinal: string) => {
    rows.push({
      id: makeFieldRowId(type.id, String(field.id)),
      typeId: type.id,
      fieldId: String(field.id),
      fieldType: field.type,
      ordinal,
      label: field.caption || '',
      // Unrestricted reads as allowed, which is true - but the two are not the same
      // state, and the caller needs isUnrestricted to say what unticking would mean.
      ...readAllowance(field.filterOptions, recordId),
    })
  }

  ;(type.workflowSteps ?? []).forEach(step => {
    const stepFields = (step?.fields ?? []).filter(belongsHere)
    const conditionalValues = (step?.conditionalFields ?? []).flatMap(
      conditional => (conditional?.values ?? []).filter(belongsHere),
    )

    if (!stepFields.length && !conditionalValues.length) {
      return
    }

    stepNumber++

    stepFields.forEach((field, index) => {
      push(field, `${stepNumber}.${index + 1}`)
    })

    // Numbered from the parent's position rather than continuing the step's sequence, so a
    // conditional value never collides with a plain field of the same step.
    ;(step?.conditionalFields ?? []).forEach((conditional, parentIndex) => {
      ;(conditional?.values ?? [])
        .filter(belongsHere)
        .forEach((field, index) => {
          push(
            field,
            `${stepNumber}.${stepFields.length + parentIndex + 1}.${index + 1}`,
          )
        })
    })
  })

  return rows
}
